import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
	ShoppingItem,
	SofaConfiguration,
	Settings,
	Platform,
	DeviceType,
	Orientation,
	RangeSettings,
	WriteDirection,
	CoverItem,
	Lightbox,
	SofaConfigurationItem,
	SofaRangeName,
	ShoppingItemType,
	ProductItem,
	VisibleModal,
	YmalSectionType,
	YmalType,
	SofaRange,
	SceneItemData,
} from 'sofa-reborn-react';
import { IframeUtility } from '@inter-ikea-kompis/utilities';
import { ShoppingCartException } from '@inter-ikea-kompis/exceptions';
import ISummaryPageVisibleModal from '@inter-ikea-kompis/component-summary-page/lib/types/ISummaryPageVisibleModal';
import SummaryPage from '../components/SummaryPage';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { additionalCoverItemRemoved, setAdditionalCoverItems } from '../store/cover';
import { setStoreId, setZipCode } from '../store/localization/actions';
import { localeService, configurationService, UrlService } from '../services';
import { ProductUtils } from '../util/ProductUtils/ProductUtils';
import {
	setCurrentLightbox,
	updateIframeHeight,
	resetIframeHeight,
	successAddToBag,
	doneSaveVpc,
	successAddToBagYmal,
} from '../store/application/actions';
import {
	fetchVpcSuccess,
	setVisibleModal,
	setSavedVpcConfiguration,
	copyCode,
	setShowAddTo,
	sortProductList,
	summaryPageExpandProduct,
} from '../store/checkout/actions';

import {
	ProductService,
	VpcService,
	ShoppingCartService,
	ShoppingListService,
	StoreService,
	CheckoutAvailabilityService,
	ZipValidationService,
	KompisDefaultFields,
} from '@inter-ikea-kompis/services';
import {
	ICart,
	IShoppingList,
	IShoppingProduct,
	IProduct,
	IStore,
	IStoreAvailability,
	IZipAvailability,
	IVpcConfiguration,
	IDexfSettings,
	ITranslations,
	IFailedShoppingItem,
} from '@inter-ikea-kompis/types';
import {
	ConfirmationSummaryShareDesignStateEnum,
	ConfigurationSummaryConfirmationCardTypeEnum,
	ConfigurationSummaryModalEnum,
	ConfigurationSummaryToastMessage,
	ConfigurationSummaryConfirmationCardStateEnum,
} from '@inter-ikea-kompis/component-configuration-summary';
import { AddToBagStateEnum, AddToBagToastMessage } from '@inter-ikea-kompis/component-add-to-bag';
import {
	InlineMessageIconEnum,
	ISummaryPageInlineMessage,
	SummaryPageModalEnum,
} from '@inter-ikea-kompis/component-summary-page';
import { ToastManager } from '@inter-ikea-kompis/component-toast';
import {
	FamilyType,
	ProductData,
	Product,
} from '@ikea-aoa/component-view-3d/lib/main/connection-manager/types';
import { Groups, ModelData } from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import { ZipInCardStateEnum, ZipInToastMessage } from '@inter-ikea-kompis/component-zip-in/';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import SummaryPageTabEnum from '@inter-ikea-kompis/component-summary-page/lib/enums/SummaryPageTabEnum';
import VpcThunk from '../store/application/thunk/VpcThunk';
import { ProductConverter } from '../util/ProductConverter';

let toastManager: ToastManager | null = null;
let addToOrigin: string | null = null;
let previousAdditionalCoverItems: ShoppingItem[] = [];
const sofaFields = [...KompisDefaultFields, ProductOptionalFieldNameEnum.complianceInformation];

const SummaryPageContainer: React.FC = () => {
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const products = useSelector<RootState, ProductData>((state) => state.products.products);
	const sofaModels = useSelector<RootState, ModelData>((state) => state.models.sofaModels);
	const sofaGroups = useSelector<RootState, Groups>((state) => state.models.sofaGroups);
	const appView = useSelector<RootState, string>((state) => state.application.appView);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const savedVpcConfiguration = useSelector<RootState, IVpcConfiguration | null>(
		(state) => state.checkout.savedVpcConfiguration
	);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const storeId = useSelector<RootState, string>((state) => state.localization.storeId as string);
	const zipCode = useSelector<RootState, string>((state) => state.localization.zipCode as string);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const additionalCoverItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.cover.additionalCoverItems
	);
	const selectedCover = useSelector<RootState, CoverItem>(
		(state) => state.cover.selectedCover as CoverItem
	);
	const sofaConfiguration = useSelector<RootState, SofaConfiguration>(
		(state) => state.sofaConfiguration.sofaConfiguration
	);
	const shoppingItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.sofaConfiguration.shoppingItems
	);
	const screenshot = useSelector<RootState, string>((state) => state.checkout.screenshot as string);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const visibleModal = useSelector<RootState, VisibleModal | null>(
		(state) => state.checkout.visibleModal
	);
	const bannerHasBeenShown = useSelector<RootState, boolean>(
		(state) => state.popOver.bannerHasBeenShown
	);
	const noSummaryPageBackButton = useSelector<RootState, boolean>(
		(state) => state.checkout.noSummaryPageBackButton
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const vpcCode = useSelector<RootState, string>((state) => state.checkout.vpcCode as string);
	const showBanner =
		localeService.getApplicationSetting('plannerBannerMessageEnabled')?.toString() === 'true' ||
		false;
	const selectedArmrest = useSelector<RootState, Product | null>(
		(state) => state.armrest.selectedArmrest
	);
	const features = useSelector<RootState, string[] | null>((state) => state.settings.features);
	const moveYmal = features && features.includes('moveymal') ? true : false;
	const stockedIkeaPlus = useSelector<RootState, boolean>(
		(state) => state.checkout.stockedIkeaPlus
	);
	const sceneItems = useSelector<RootState, SceneItemData[]>((state) => state.scene.sceneItems);
	const summaryRef = useRef<HTMLDivElement | null>(null);
	const dispatch = useDispatch<AppDispatch>();

	const [shoppingProducts, setShoppingProducts] = useState<IShoppingProduct[] | []>([]);
	const [additionalCoverProducts, setAdditionalCoverProducts] = useState<IShoppingProduct[] | []>(
		[]
	);
	const [ymalProducts, setYmalProducts] = useState<IProduct[] | []>([]);
	const [includedProducts, setIncludedProducts] = useState<IProduct[] | []>([]);
	const [availabilitiesProducts, setAvailabilitiesProducts] = useState<ShoppingItem[] | null>(null);
	const [stores, setStores] = useState<IStore[] | []>([]);
	const [storeAvailabilities, setStoreAvailabilites] = useState<IStoreAvailability[] | null>(null);

	const [zipAvailabilities, setZipAvailabilities] = useState<IZipAvailability[] | null>(null);
	const [zipState, setZipState] = useState(ZipInCardStateEnum.default);
	const [zipInputValue, setZipInputValue] = useState<string | null>(null);
	const [saveVpcState, setSaveVpcState] = useState<ConfirmationSummaryShareDesignStateEnum>(
		ConfirmationSummaryShareDesignStateEnum.default
	);
	const [addToCartState, setAddToCartState] = useState<AddToBagStateEnum>(
		AddToBagStateEnum.default
	);
	const [addToListState, setAddToListState] = useState<AddToBagStateEnum>(
		AddToBagStateEnum.default
	);
	const [confirmationCardType, setConfirmationCardType] =
		useState<ConfigurationSummaryConfirmationCardTypeEnum | null>(null);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [confirmationCardState, setConfirmationCardState] = useState(
		ConfigurationSummaryConfirmationCardStateEnum.default
	);
	const [inlineMessages, setInlineMessages] = useState<ISummaryPageInlineMessage[]>([]);
	const [selectedTab, setSelectedTab] = useState<SummaryPageTabEnum>(
		SummaryPageTabEnum.productList
	);

	const [failedShoppingItems, setFailedShoppingItems] = useState<IFailedShoppingItem[] | null>(
		null
	);
	const [addUnavailableProductsToList, setAddUnavailableProductsToList] = useState(false);

	const shoppingCartService = new ShoppingCartService({
		locale: settings.locale,
		localizationPrefixApplication: settings.localizationPrefixApplication,
		localizationEndpoints: settings.localizationEndpoints,
		applicationName: settings.applicationName,
		consumer: settings.consumer,
		contract: settings.contract,
	});

	const shoppingListService = new ShoppingListService({
		locale: settings.locale,
		localizationPrefixApplication: settings.localizationPrefixApplication,
		localizationEndpoints: settings.localizationEndpoints,
		consumer: settings.consumer,
		contract: settings.contract,
	});

	async function fetchAddToCart(event: CustomEvent, designCode: string): Promise<void> {
		setAddToCartState(AddToBagStateEnum.loading);
		setConfirmationCardState(ConfigurationSummaryConfirmationCardStateEnum.default);

		let shoppingCart: ICart = { cartCount: 0, errorList: [] };
		let error: ShoppingCartException | null = null;

		try {
			shoppingCart = await shoppingCartService.addToCart({
				items: event.detail.shoppingItems,
				designCode,
			});
		} catch (e) {
			error = e;
		}

		// Handles the Nif Cart API
		if (error && error?.failedShoppingItems?.length > 0) {
			setFailedShoppingItems(error?.failedShoppingItems);
			dispatch(
				setVisibleModal({
					itemId: null,
					modal: ConfigurationSummaryModalEnum.unavailableProductCard,
				})
			);
		} else if (error || shoppingCart.errorList.length > 0) {
			setAddToCartState(AddToBagStateEnum.default);
			if (kompisTranslations) {
				toastManager = new ToastManager({ theme: SkapaTheme });
				toastManager.showToast(
					ConfigurationSummaryToastMessage.getAddToCartFail(kompisTranslations.kompis)
				);
			}
		} else {
			// Auto-save the design to the user's profile if the current design isn't saved already.
			// @TODO: saveDesign({ showToastOnFailure: false });
			setAddToCartState(AddToBagStateEnum.confirmation);
			setConfirmationCardType(ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess);
			dispatch(
				setVisibleModal({ itemId: null, modal: ConfigurationSummaryModalEnum.confirmationCard })
			);
			dispatch(successAddToBag(event, additionalCoverProducts.length > 0));
		}
	}

	async function fetchAddToList(e: CustomEvent): Promise<void> {
		setAddToListState(AddToBagStateEnum.loading);

		let list: IShoppingList | null = null;
		try {
			list = await shoppingListService.addToList({
				items: [...shoppingItems, ...additionalCoverItems],
			});
		} catch (error) {
			setAddToListState(AddToBagStateEnum.default);
			const toastManager = new ToastManager({ theme: this.theme });
			if (kompisTranslations) {
				toastManager.showToast(
					ConfigurationSummaryToastMessage.getAddToListFail(kompisTranslations.kompis)
				);
				throw error;
			}
		}
		if (kompisTranslations && list && list.errorList && list.errorList.length > 0) {
			setAddToListState(AddToBagStateEnum.default);
			const toastManager = new ToastManager({ theme: this.theme });
			toastManager.showToast(
				ConfigurationSummaryToastMessage.getAddToListFail(kompisTranslations.kompis)
			);
		} else {
			setAddToListState(AddToBagStateEnum.confirmation);
			setConfirmationCardType(ConfigurationSummaryConfirmationCardTypeEnum.addToListSuccess);
			dispatch(
				setVisibleModal({ modal: ConfigurationSummaryModalEnum.confirmationCard, itemId: null })
			);
			dispatch(successAddToBag(e, additionalCoverProducts.length > 0));
		}
	}

	async function fetchSaveVpc(): Promise<string | null> {
		if (
			savedVpcConfiguration &&
			JSON.stringify(savedVpcConfiguration.configuration) === JSON.stringify(sofaConfiguration) &&
			savedVpcConfiguration.configuration['zipCode'] === zipCode
		) {
			dispatch(fetchVpcSuccess(savedVpcConfiguration.configurationId));
			dispatch(doneSaveVpc(savedVpcConfiguration.configurationId));

			setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
			return savedVpcConfiguration.configurationId;
		} else {
			const vpcService = new VpcService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
				applicationName: settings.applicationName,
				dexfApplicationId: settings.dexfApplicationId,
				dexfConfigurationVersion: settings.dexfConfigurationVersion,
			});
			const icf = configurationService.getICF(
				sofaConfiguration,
				products,
				sofaModels,
				sofaGroups,
				rangeSettings
			);

			if (zipCode) {
				sofaConfiguration.zipCode = zipCode;
			}
			if (coverOnly) {
				sofaConfiguration.coverOnly = true;
			}

			try {
				const vpcConfiguration = await vpcService.saveConfiguration(
					sofaConfiguration,
					[...shoppingItems, ...additionalCoverItems],
					screenshot,
					icf
				);

				if (vpcConfiguration) {
					dispatch(fetchVpcSuccess(vpcConfiguration.configurationId));
					dispatch(doneSaveVpc(vpcConfiguration.configurationId));
					dispatch(setSavedVpcConfiguration(vpcConfiguration));
					setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
					return vpcConfiguration.configurationId;
				} else {
					setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
					return null;
				}
			} catch {
				setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
				return null;
			}
		}
	}

	function resetSaveForLater(): void {
		setConfirmationCardType(null);
	}

	const onStoreChanged = (e: CustomEvent): void => {
		dispatch(setStoreId(e.detail.storeId));
	};

	const getQrCodeUrl = (): string => {
		const isProduction = ApplicationUtils.isProduction();
		const plannerUrl = localeService.getApplicationSetting('plannerUrl') as string;
		const url = plannerUrl
			? plannerUrl
					.replace('[[countrycode]]', localeService.getCountryCode())
					.replace('[[languagecode]]', localeService.getLanguageCode())
					.replace('[[applicationname]]', rangeSettings.rangeId as SofaRange)
			: null;
		const partOfUrl = url ? url : getPartOfUrl(isProduction);
		let qrCodeUrl = '';

		if (isProduction) {
			qrCodeUrl =
				partOfUrl +
				`${!plannerUrl ? '?showSummaryPage' : ''}` +
				`${storeId ? (!plannerUrl ? '&storeId=' + storeId : '?storeId=' + storeId) : ''}` +
				`${
					zipCode
						? storeId || !plannerUrl
							? '&zipCode=' + zipCode?.replace(/\s/g, '')
							: '?zipCode=' + zipCode?.replace(/\s/g, '')
						: ''
				}` +
				`#${vpcCode}`;
		} else {
			qrCodeUrl =
				partOfUrl +
				'?range=' +
				UrlService.getSofaRange() +
				'&locale=' +
				localeService.getLanguageCode() +
				'_' +
				localeService.getCountryCode().toUpperCase() +
				`${!plannerUrl ? '&showSummaryPage' : ''}` +
				`${storeId ? '&storeId=' + storeId : ''}` +
				`${zipCode ? '&zipCode=' + zipCode?.replace(/\s/g, '') : ''}` +
				`#${vpcCode}`;
		}

		return qrCodeUrl;
	};

	const getPartOfUrl = (isProduction: boolean): string => {
		if (isProduction) {
			return (
				window.location.host +
				'/addon-app/sofas/' +
				UrlService.getSofaRange() +
				'/web/latest/' +
				localeService.getCountryCode() +
				'/' +
				localeService.getLanguageCode()
			);
		} else {
			return window.location.host + window.location.pathname;
		}
	};

	async function showSaveForLater(): Promise<void> {
		if (
			savedVpcConfiguration &&
			JSON.stringify(savedVpcConfiguration.configuration) === JSON.stringify(sofaConfiguration) &&
			savedVpcConfiguration.configuration['zipCode'] === zipCode
		) {
			resetSaveForLater();
			dispatch(setCurrentLightbox('save-for-later'));

			dispatch(fetchVpcSuccess(savedVpcConfiguration.configurationId));
			dispatch(doneSaveVpc(savedVpcConfiguration.configurationId));

			setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
		} else {
			const vpcService = new VpcService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
				applicationName: settings.applicationName,
				dexfApplicationId: settings.dexfApplicationId,
				dexfConfigurationVersion: settings.dexfConfigurationVersion,
			});
			const icf = configurationService.getICF(
				sofaConfiguration,
				products,
				sofaModels,
				sofaGroups,
				rangeSettings
			);

			resetSaveForLater();
			setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.loading);
			dispatch(setCurrentLightbox('save-for-later'));

			if (zipCode) {
				sofaConfiguration.zipCode = zipCode;
			}
			if (coverOnly) {
				sofaConfiguration.coverOnly = true;
			}

			try {
				const vpcConfiguration = await vpcService.saveConfiguration(
					sofaConfiguration,
					[...shoppingItems, ...additionalCoverItems],
					screenshot,
					icf
				);

				if (vpcConfiguration) {
					dispatch(fetchVpcSuccess(vpcConfiguration.configurationId));
					dispatch(doneSaveVpc(vpcConfiguration.configurationId));
					dispatch(setSavedVpcConfiguration(vpcConfiguration));
				}

				setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
			} catch {
				setSaveVpcState(ConfirmationSummaryShareDesignStateEnum.default);
				setConfirmationCardType(ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail);
			}
		}
	}

	async function showAddToCart(e: CustomEvent): Promise<void> {
		const code = await fetchSaveVpc();
		if (code) {
			await fetchAddToCart(e, code).catch(() => {
				// Show prompt that something did not work
				dispatch(setCurrentLightbox('failed-prompt'));
			});
		}
	}

	async function showAddToList(e: CustomEvent): Promise<void> {
		await fetchSaveVpc()
			.then(() => fetchAddToList(e))
			.catch(() => {
				// Show prompt that something did not work
				dispatch(setCurrentLightbox('failed-prompt'));
			});
	}

	const onAddUnavailableToCartAndList = (event: CustomEvent) => {
		setAddToCartState(AddToBagStateEnum.loading);
		setAddToListState(AddToBagStateEnum.loading);

		const { shoppingCartItems, shoppingListItems } = event.detail;

		Promise.all([
			shoppingCartService.addToCart({
				items: shoppingCartItems,
			}),
			shoppingListService.addToList({ items: shoppingListItems }),
		])
			.then(([shoppingCart, shoppingList]) => {
				toastManager = new ToastManager({ theme: SkapaTheme });

				const hasErrors = (cartOrList: ICart | IShoppingList): boolean => {
					return !!cartOrList.errorList.length;
				};

				if (hasErrors(shoppingCart) && !hasErrors(shoppingList)) {
					setAddToCartState(AddToBagStateEnum.default);
					setAddToListState(AddToBagStateEnum.default);
					setConfirmationCardType(
						ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartFailAndListSuccess
					);
					setVisibleModal({ itemId: null, modal: ConfigurationSummaryModalEnum.confirmationCard });
				} else if (hasErrors(shoppingList) && !hasErrors(shoppingCart)) {
					setAddToCartState(AddToBagStateEnum.default);
					setAddToListState(AddToBagStateEnum.default);
					setConfirmationCardType(
						ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail
					);
					setVisibleModal({ itemId: null, modal: ConfigurationSummaryModalEnum.confirmationCard });
				} else if (hasErrors(shoppingCart) && hasErrors(shoppingList)) {
					if (kompisTranslations) {
						toastManager.showToast(
							ConfigurationSummaryToastMessage.getAddToCartAndListFail(kompisTranslations.kompis)
						);
					}
					setAddToCartState(AddToBagStateEnum.default);
					setAddToListState(AddToBagStateEnum.default);
				} else {
					// Auto-save the design to the user's profile if the current design isn't saved already.
					// saveDesign({ showToastOnFailure: false });
					setAddToCartState(AddToBagStateEnum.confirmation);
					setAddToListState(AddToBagStateEnum.confirmation);
					setConfirmationCardType(
						ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess
					);
					setVisibleModal({ itemId: null, modal: ConfigurationSummaryModalEnum.confirmationCard });
				}
			})
			.catch(() => {
				toastManager = new ToastManager({ theme: SkapaTheme });

				if (kompisTranslations) {
					toastManager.showToast(
						ConfigurationSummaryToastMessage.getAddToCartFail(kompisTranslations.kompis)
					);
				}
				setAddToCartState(AddToBagStateEnum.default);
				setAddToListState(AddToBagStateEnum.default);
			});
	};

	async function showSaveVpc(): Promise<void> {
		showSaveForLater();
	}

	async function getYmalProducts(): Promise<void> {
		const ymalProductItems = rangeSettings.ymalProducts
			? rangeSettings.ymalProducts[selectedCover.coverName]
			: null;
		if (ymalProductItems) {
			const filteredProducts = Object.values(products).filter((product) =>
				sofaConfiguration.configuration?.children?.some(
					(child) => product.productId === child.articleId || product.previousId === child.articleId
				)
			);
			const ymalProductItemsSofaBed: ProductItem[] = getYmalSofabedItems(
				filteredProducts,
				moveYmal
			);
			const ymalProductItemsStorage: ProductItem[] = getYmalStorageItems(
				filteredProducts,
				moveYmal
			);
			const ymalProductItemsWideArmrest: ProductItem[] = getYmalWideArmrestItems();
			const ymalProductItemsHeadrest: ProductItem[] = getYmalHeadrestItems();
			const ymalProductItemsLaptop: ProductItem[] = getYmalLaptopItems();
			const productService = new ProductService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});
			const productItemArray = moveYmal
				? [
						ymalProductItems[0],
						...ymalProductItemsWideArmrest,
						...ymalProductItemsLaptop,
						...ymalProductItemsHeadrest,
						...ymalProductItems.slice(1),
				  ]
				: [
						ymalProductItems[0],
						...ymalProductItemsSofaBed,
						...ymalProductItemsStorage,
						...ymalProductItemsWideArmrest,
						...ymalProductItemsLaptop,
						...ymalProductItemsHeadrest,
						...ymalProductItems.slice(1),
				  ];
			const ymalProducts = await productService.getProducts(productItemArray, sofaFields);
			const filteredYmalProducts = ymalProducts.filter((ymalProduct) => ymalProduct.valid);
			setYmalProducts(filteredYmalProducts);
		} else {
			setYmalProducts([]);
		}
	}

	async function getStoreAvailabilities(): Promise<void> {
		if (storeId && availabilitiesProducts) {
			const service = new CheckoutAvailabilityService({
				locale: settings.locale,
				localizationEndpoints: settings.localizationEndpoints,
			});

			const availabilities = await service.getStoreAvailabilities(storeId, availabilitiesProducts);
			setStoreAvailabilites(availabilities);
		} else {
			setStoreAvailabilites(null);
		}
	}

	async function getShoppingProducts(): Promise<void> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const childItems: ShoppingItem[] = [];

		const shoppingProducts = await productService.getShoppingProducts(
			shoppingItems,
			KompisDefaultFields
		);

		if (coverOnly) {
			const coverOnlyArticles: ShoppingItem[] = ProductUtils.getCoverOnlyArticles(shoppingProducts);
			const articleProducts = await productService.getShoppingProducts(
				coverOnlyArticles,
				KompisDefaultFields
			);
			const filteredCoverProducts = articleProducts.filter(
				(prod) => prod.product.valid && prod.product.content?.mainTypeCode === '00062'
			);

			setShoppingProducts(filteredCoverProducts);
		} else {
			const filteredShoppingProducts = shoppingProducts.filter(
				(shoppingProduct) => shoppingProduct.product.valid
			);
			setShoppingProducts(filteredShoppingProducts);
		}

		shoppingProducts.forEach((shoppingProduct: IShoppingProduct): void => {
			if (shoppingProduct.product.content && shoppingProduct.product.content.child) {
				shoppingProduct.product.content.child.forEach((child) => {
					const splitChildId = child.itemId.split('-');

					// Don't add duplicates of included products
					if (!childItems.some((childItem) => childItem.id === splitChildId[1])) {
						const childItem = {
							id: splitChildId[1],
							quantity: child.quantity,
							type: splitChildId[0] as ShoppingItemType,
						};

						childItems.push(childItem);
					}
				});
			}
		});

		const includedProducts = await productService.getShoppingProducts(
			childItems,
			KompisDefaultFields
		);
		const formattedIncludedProducts = includedProducts.map((includedProduct) => {
			return includedProduct.product;
		});

		setIncludedProducts(formattedIncludedProducts);
		setAvailabilitiesProducts([...shoppingItems, ...childItems, ...additionalCoverItems]);
		setAdditionalCoverProducts(
			await ProductUtils.getAdditionalCoverProducts(settings, additionalCoverItems)
		);
	}

	async function getStores(): Promise<void> {
		const storeService = new StoreService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
			consumer: settings.consumer,
			contract: settings.contract,
		});
		setStores(await storeService.getStores());
	}

	async function onYmalAddToCart(
		shoppingItems: ShoppingItem[],
		settings: Settings,
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	): Promise<void> {
		const shoppingCartService = new ShoppingCartService(settings);
		const cart = await shoppingCartService.addToCart({ items: shoppingItems });
		toastManager = new ToastManager({ theme: SkapaTheme });
		if (kompisTranslations && kompisSettings) {
			if (cart.errorList.length > 0) {
				toastManager.showToast(
					AddToBagToastMessage.getAddToCartFailure(
						kompisTranslations.kompis,
						kompisSettings.kompis.urls
					)
				);
			} else {
				toastManager.showToast(
					AddToBagToastMessage.getAddToCartSuccess(
						kompisTranslations.kompis,
						kompisSettings.kompis.urls
					)
				);
				dispatch(
					successAddToBagYmal({
						shoppingItems: shoppingItems,
						type: types.ymalType,
						sectionType: types.ymalSectionType,
					})
				);
			}
		}
	}

	async function onYmalAddToList(
		shoppingItems: ShoppingItem[],
		settings: Settings,
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	): Promise<void> {
		const shoppingListService = new ShoppingListService(settings);
		const shoppingList = await shoppingListService.addToList({ items: shoppingItems });
		const toastManager = new ToastManager({ theme: SkapaTheme });
		if (kompisTranslations && kompisSettings) {
			if (shoppingList.errorList.length > 0) {
				toastManager.showToast(
					AddToBagToastMessage.getAddToCartFailure(
						kompisTranslations.kompis,
						kompisSettings.kompis.urls
					)
				);
			} else {
				toastManager.showToast(
					AddToBagToastMessage.getAddToCartSuccess(
						kompisTranslations.kompis,
						kompisSettings.kompis.urls
					)
				);
				dispatch(
					successAddToBagYmal({
						shoppingItems: shoppingItems,
						type: types.ymalType,
						sectionType: types.ymalSectionType,
					})
				);
			}
		}
	}

	const onOpenZipIn = (): void => {
		dispatch(setVisibleModal({ modal: SummaryPageModalEnum.zipIn, itemId: null }));
	};

	const onModalOpen = (e: CustomEvent): void => {
		dispatch(
			setVisibleModal({
				modal: e.detail.visibleModal.modal ? e.detail.visibleModal.modal : e.detail.visibleModal,
				itemId: e.detail.visibleModal.itemId ? e.detail.visibleModal.itemId : null,
			})
		);
	};

	const onModalClose = (): void => {
		setConfirmationCardState(ConfigurationSummaryConfirmationCardStateEnum.default);
		setAddToCartState(AddToBagStateEnum.default);
		setAddToListState(AddToBagStateEnum.default);
		dispatch(setVisibleModal(null));
	};

	const onSortListChange = (e: CustomEvent): void => {
		dispatch(sortProductList(e.detail.selected));
	};

	const getSortedSofaConfiguration = (
		children: SofaConfigurationItem[]
	): SofaConfigurationItem[] => {
		children.forEach((child) => {
			if (child.pos) {
				/* Change positions to only include one decimal */
				child.pos.x = parseFloat((child.pos.x as number).toFixed(1));
				child.pos.y = parseFloat((child.pos.y as number).toFixed(1));
				child.pos.z = parseFloat((child.pos.z as number).toFixed(1));
				children['children'] = getSortedSofaConfiguration(
					child.children as SofaConfigurationItem[]
				);
			}
		});
		return children.sort((a, b) => {
			if (a.pos && b.pos) {
				return (a.pos.x as number) - (b.pos.x as number);
			}
			return 0;
		});
	};

	const getSofaConfiguration = (sofaConfiguration: SofaConfiguration): SofaConfiguration => {
		if (
			sofaConfiguration &&
			sofaConfiguration.configuration &&
			sofaConfiguration.configuration.children
		) {
			sofaConfiguration.configuration.children = getSortedSofaConfiguration(
				sofaConfiguration.configuration.children
			);
		}
		return sofaConfiguration;
	};

	const onConfirmationCardStateChanged = (e: CustomEvent): void => {
		setConfirmationCardState(e.detail.selectedState);
	};

	function onZipInput(event: CustomEvent): void {
		setZipInputValue(event.detail.inputValue);
		setZipState(event.detail.state);
		if (event.detail.zipCode === null) {
			dispatch(setZipCode(null, true));
			setZipAvailabilities(null);
		}
	}

	async function getZipAvailabilities(sceneItems: SceneItemData[]): Promise<void> {
		if (zipCode && shoppingItems) {
			const service = new CheckoutAvailabilityService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});
			const childItems = ProductConverter.getChildItemsFromScene(
				sceneItems,
				products,
				rangeSettings
			);
			const items = [...shoppingItems, ...childItems, ...additionalCoverItems];
			try {
				const zipAvailabilities = await service.getZipAvailabilities(zipCode, items);
				setZipAvailabilities(zipAvailabilities);
			} catch (error) {
				setZipState(ZipInCardStateEnum.noZipInfo);
			}
		} else {
			setZipAvailabilities(null);
		}
	}

	async function onZipSubmit(e: CustomEvent): Promise<void> {
		const zipInputValue = e.detail.zipCode;
		if (
			!e.detail.zipCode &&
			e.detail.state === ZipInCardStateEnum.default &&
			!e.detail.visibleModal
		) {
			setZipState(ZipInCardStateEnum.default);
			setZipAvailabilities(null);
			dispatch(setZipCode(null, true));
			dispatch(setVisibleModal(null));
			return;
		}
		if (!zipInputValue) {
			setZipState(ZipInCardStateEnum.noZipInfo);
			return;
		}
		const service = new ZipValidationService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		setZipState(ZipInCardStateEnum.loading);
		try {
			const zipValidation = await service.getZipValidation(zipInputValue);
			if (zipValidation.valid && zipValidation.content) {
				dispatch(setZipCode(zipValidation.content.formatted, true));
				setZipState(ZipInCardStateEnum.submitted);
				if (kompisTranslations) {
					toastManager = new ToastManager({ theme: SkapaTheme });
					toastManager.showToast(
						ZipInToastMessage.getZipCodeToastLabel(
							kompisTranslations.kompis,
							zipValidation.content.formatted
						)
					);
					dispatch(setVisibleModal(null));
				}
			} else {
				setZipState(ZipInCardStateEnum.invalidInput);
			}
		} catch (error) {
			setZipState(ZipInCardStateEnum.noZipInfo);
		}
	}

	const onAdditionalCoverClick = (): void => {
		dispatch(setCurrentLightbox('additional-cover-picker'));
	};

	const onRemoveAdditionalCoverItem = (e: CustomEvent): void => {
		dispatch(
			setAdditionalCoverItems(
				[...additionalCoverItems].filter((c) => c.id !== e.detail.itemId.replace('ART-', ''))
			)
		);
		dispatch(additionalCoverItemRemoved(e.detail.itemId));
	};

	const getYmalSofabedItems = (filteredProducts: Product[], moveYmal: boolean): ProductItem[] => {
		const sleepFamilies: FamilyType[] = ['SOFABED'];
		const containsSleep = ProductUtils.containsStorage(
			filteredProducts,
			sleepFamilies,
			rangeSettings
		);
		if (moveYmal) {
			return containsSleep && rangeSettings?.ymalProductsSofaBed?.hasOwnProperty('all')
				? rangeSettings.ymalProductsSofaBed['all']
				: [];
		} else {
			return containsSleep &&
				rangeSettings?.ymalProductsSofaBed?.hasOwnProperty(selectedCover.coverName)
				? rangeSettings.ymalProductsSofaBed[selectedCover.coverName]
				: [];
		}
	};

	const getYmalStorageItems = (filteredProducts: Product[], moveYmal: boolean): ProductItem[] => {
		const storageFamilies: FamilyType[] =
			rangeSettings.rangeName === 'KIVIK' ? ['FOOTSTOOL'] : ['CHAISE', 'FOOTSTOOL'];
		const containsStorage =
			ProductUtils.containsStorage(filteredProducts, storageFamilies, rangeSettings) ||
			rangeSettings.rangeName === 'JÄTTEBO';
		if (moveYmal) {
			return containsStorage && rangeSettings?.ymalProductsStorage?.hasOwnProperty('all')
				? rangeSettings.ymalProductsStorage['all']
				: [];
		} else {
			return containsStorage && rangeSettings?.ymalProductsStorage?.hasOwnProperty('allOld')
				? rangeSettings.ymalProductsStorage['allOld']
				: [];
		}
	};

	const getYmalWideArmrestItems = (): ProductItem[] => {
		const hasArmrest = ProductUtils.hasArmrest(products, shoppingItems);
		const containsWideArmrest =
			(rangeSettings.showArmrestTrayInYmal &&
				(rangeSettings.rangeName === 'KIVIK' || hasArmrest)) ||
			(selectedArmrest && selectedArmrest.modelId.includes('WIDE'));
		return containsWideArmrest &&
			rangeSettings?.ymalProductsWideArmrest?.hasOwnProperty(selectedCover.coverName)
			? rangeSettings.ymalProductsWideArmrest[selectedCover.coverName]
			: [];
	};

	const getYmalHeadrestItems = (): ProductItem[] => {
		const hasSofaPart = ProductUtils.hasSofaPart(products, shoppingItems);
		return rangeSettings.showHeadrestInYmal &&
			hasSofaPart &&
			rangeSettings?.ymalProductsHeadrest?.hasOwnProperty(selectedCover.coverName)
			? rangeSettings.ymalProductsHeadrest[selectedCover.coverName]
			: [];
	};

	const getYmalLaptopItems = (): ProductItem[] => {
		return rangeSettings.showLaptopTrayInYmal &&
			rangeSettings?.ymalProductsLaptopTray?.hasOwnProperty('all')
			? rangeSettings.ymalProductsLaptopTray['all']
			: [];
	};

	const getIfSolutionContainsStorage = (): boolean => {
		const filteredProducts = Object.values(products).filter((product) =>
			sofaConfiguration.configuration?.children?.some(
				(child) => product.productId === child.articleId || product.previousId === child.articleId
			)
		);
		const storageFamilies: FamilyType[] =
			rangeSettings.rangeName === 'KIVIK' ? ['FOOTSTOOL'] : ['CHAISE', 'FOOTSTOOL'];
		return (
			ProductUtils.containsStorage(filteredProducts, storageFamilies, rangeSettings) ||
			rangeSettings.rangeName === 'JÄTTEBO'
		);
	};
	const getIfSolutionContainsSleep = (): boolean => {
		const filteredProducts = Object.values(products).filter((product) =>
			sofaConfiguration.configuration?.children?.some(
				(child) => product.productId === child.articleId || product.previousId === child.articleId
			)
		);
		return (
			ProductUtils.containsFamily(filteredProducts, 'SOFABED') ||
			ProductUtils.modelIdContains(filteredProducts, 'SLEEP')
		);
	};

	useEffect(() => {
		if (appView !== 'summary-page' || !summaryRef.current) {
			return undefined;
		}

		// Since we have a timeout for taking the screenshot we need to have the same timeout before we resize iframe
		const timeout = setTimeout(() => {
			if (summaryRef.current) {
				if (
					visibleModal ||
					currentLightbox === 'save-for-later' ||
					currentLightbox === 'additional-cover-picker'
				) {
					if (currentLightbox === 'additional-cover-picker' && orientation === 'landscape') {
						const calculatedHeight = IframeUtility.calculateHeight(window.innerWidth);
						dispatch(updateIframeHeight(calculatedHeight + 200));
					} else {
						dispatch(resetIframeHeight());
					}
				} else {
					dispatch(updateIframeHeight(summaryRef.current.offsetHeight + 1));
				}
			}
		}, 300);

		return (): void => {
			clearTimeout(timeout);
		};
	}, [visibleModal, currentLightbox, appView, orientation, summaryRef.current]);

	useEffect(() => {
		if (appView === 'summary-page') {
			getShoppingProducts();
			const newInlineMessages: ISummaryPageInlineMessage[] = [];

			if (coverOnly) {
				const coverOnlyMessage: ISummaryPageInlineMessage = {
					title: localeService.getTranslation('coverOnlyDisclaimerTitle'),
					message: localeService.getTranslation('coverOnlyDisclaimerText'),
					icon: InlineMessageIconEnum.information,
				};
				newInlineMessages.push(coverOnlyMessage);
			}
			if (
				localeService.getApplicationSetting('showCoverDisclaimer') &&
				selectedCover.isIkeaPlus &&
				!stockedIkeaPlus
			) {
				const ikeaPlusMessage: ISummaryPageInlineMessage = {
					title: localeService.getTranslation('coverDisclaimerTitle'),
					message: localeService.getTranslation('coverDisclaimerInfo'),
					icon: InlineMessageIconEnum.information,
				};

				newInlineMessages.push(ikeaPlusMessage);
			}

			setInlineMessages(newInlineMessages);
		} else {
			setInlineMessages([]);
		}

		if (platform === 'kiosk') {
			if (document) {
				const element = document.getElementById('productList');
				if (element) {
					element.scrollTop = 0;
				}
			} else {
				throw 'SummaryPageContainer: useEffect(): document does not exist';
			}
		} else if (appView !== 'summary-page') {
			setSelectedIndex(0);
		}
	}, [appView, stockedIkeaPlus]);

	useEffect(() => {
		getShoppingProducts();
		getYmalProducts();
		getStores();
	}, [shoppingItems, storeId, coverOnly]);

	useEffect(() => {
		getStoreAvailabilities();
	}, [storeId, availabilitiesProducts]);

	useEffect(() => {
		getZipAvailabilities(sceneItems);
	}, [zipCode, availabilitiesProducts, shoppingItems]);

	useEffect(() => {
		getShoppingProducts();
	}, [additionalCoverItems]);

	useEffect(() => {
		if (additionalCoverProducts.length > 0) {
			setSelectedTab(SummaryPageTabEnum.productList);
		}
		if (
			platform === 'kiosk' &&
			appView === 'summary-page' &&
			additionalCoverItems.length > 0 &&
			((previousAdditionalCoverItems.length === 0 && additionalCoverItems.length > 0) ||
				JSON.stringify(previousAdditionalCoverItems) !== JSON.stringify(additionalCoverItems))
		) {
			const vpcThunk = new VpcThunk();
			dispatch(vpcThunk.fetch(true));
			previousAdditionalCoverItems = additionalCoverItems;
		}
	}, [additionalCoverProducts]);

	return (
		<SummaryPage
			ref={summaryRef}
			additionalCovers={additionalCoverProducts}
			hide={appView !== 'summary-page'}
			sofaConfiguration={getSofaConfiguration(sofaConfiguration)}
			shoppingItems={shoppingItems}
			settings={settings}
			platform={platform}
			orientation={orientation}
			deviceType={deviceType}
			writeDirection={writeDirection}
			saveVpcState={saveVpcState}
			addToCartState={addToCartState}
			addToListState={addToListState}
			confirmationCardType={confirmationCardType}
			ymalProducts={ymalProducts}
			sofaRangeName={rangeSettings.rangeName as SofaRangeName}
			zipState={zipState}
			zipInputValue={zipInputValue}
			stores={stores}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			shoppingProducts={shoppingProducts}
			includedProducts={includedProducts}
			storeAvailabilities={storeAvailabilities}
			zipAvailabilities={zipAvailabilities}
			zipCode={zipCode}
			selectedIndex={selectedIndex}
			showSaveForLater={currentLightbox === 'save-for-later'}
			noSummaryPageBackButton={noSummaryPageBackButton}
			visibleModal={visibleModal as ISummaryPageVisibleModal}
			bannerHasBeenShown={bannerHasBeenShown}
			noValidLink={currentLightbox === 'no-valid-link'}
			screenshot={screenshot}
			selectedCover={selectedCover}
			storeId={storeId}
			showAdditionalCoverCard={
				rangeSettings.showAdditionalCoverCard && !coverOnly && selectedCover.isRemovable
					? true
					: false
			}
			vpcCode={vpcCode}
			showBanner={showBanner}
			confirmationCardState={confirmationCardState}
			additionalCoverItemIds={additionalCoverItems.map((cover) => `${cover.type}-${cover.id}`)}
			shoppingItemIds={shoppingItems.map((item) => `${item.type}-${item.id}`)}
			inlineMessages={inlineMessages}
			solutionContainsSleep={getIfSolutionContainsSleep()}
			solutionContainsStorage={getIfSolutionContainsStorage()}
			moveYmal={moveYmal}
			selectedTab={selectedTab}
			qrCodeUrl={getQrCodeUrl()}
			showTotalPackageWeight={platform === 'kiosk'}
			onAddToCart={(e: CustomEvent): void => {
				addToOrigin = 'cart';
				dispatch(setShowAddTo({ show: true, origin: 'cart' }));
				showAddToCart(e);
			}}
			onAddToList={(e: CustomEvent): void => {
				addToOrigin = 'list';
				dispatch(setShowAddTo({ show: true, origin: 'list' }));
				showAddToList(e);
			}}
			failedShoppingItems={failedShoppingItems}
			addUnavailableProductsToList={addUnavailableProductsToList}
			onAddUnavailableProductsToListChange={(event) =>
				setAddUnavailableProductsToList(event.detail.addUnavailableProductsToList)
			}
			onAddUnavailableToCartAndList={onAddUnavailableToCartAndList}
			onSaveVpc={showSaveVpc}
			onYmalAddToCart={(
				shoppingItems: ShoppingItem[],
				types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
			): Promise<void> => {
				return onYmalAddToCart(shoppingItems, settings, types);
			}}
			onYmalAddToList={(
				shoppingItems: ShoppingItem[],
				types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
			): Promise<void> => onYmalAddToList(shoppingItems, settings, types)}
			onModalOpen={(e: CustomEvent): void => onModalOpen(e)}
			onModalClose={(): void => {
				if (addToOrigin) {
					dispatch(setShowAddTo({ show: false, origin: addToOrigin }));
					addToOrigin = null;
				}
				onModalClose();
			}}
			onCopyDesign={(): void => {
				dispatch(copyCode());
			}}
			onProductListExpand={(e: CustomEvent): void => {
				dispatch(
					summaryPageExpandProduct(e.detail.itemId, e.detail.expanded.length > 0 ? true : false)
				);
			}}
			onZipInput={onZipInput}
			onZipSubmit={onZipSubmit}
			onConfirmationCardStateChanged={onConfirmationCardStateChanged}
			onAdditionalCoverClick={onAdditionalCoverClick}
			onRemoveAdditionalCoverItem={onRemoveAdditionalCoverItem}
			onStoreChanged={onStoreChanged}
			onSortListChange={onSortListChange}
			onOpenZipIn={onOpenZipIn}
		/>
	);
};

export default SummaryPageContainer;
